import React, { Suspense, useCallback, useEffect, useState } from "react";
import { createBrowserHistory } from "history";
import { Route } from "src/config/routes";
import { Router } from "react-router";
import * as Sentry from "@sentry/react";
import { Integrations } from "@sentry/tracing";
import { configure } from "mobx";
import { Main } from "src/screens/Main";
import { NetworkConfig } from "./network/NetworkConfig";
import { RootStore, RootStoreContext } from "src/stores/RootStore";
import { UI, initUI } from "@wwimmo/ui";
import { Loading } from "src/screens";
import { Error } from "src/screens";
import { Notifier } from "./components/notifications/Notifier";
import { ToastContainer as NotificationContainer } from "react-toastify";
import { GraphqlClient } from "./network/GraphqlClient";
import { Config } from "./config/config";
import { initializeApolloClient } from "src/network/apolloClientInstance";
import "@wwimmo/ui/dist/index.css";
import "react-toastify/dist/ReactToastify.css";
import "src/App.css";
import { Footer } from "./components/footer/Footer";
import { UserRatingModal } from "./screens/userrating/UserRatingModal";
import { UserRatingModalAssembly } from "./screens/userrating/UserRatingModalAssembly";
import Axios from "./network/Axios";
import { hasConsent } from "./utils/Consent";

configure({ enforceActions: "always" });

const history = createBrowserHistory();
const rootStore = new RootStore();
rootStore.navStore.setTitle("");
export const stores = rootStore;

initUI({
    logo: NetworkConfig.brandAssets.logoImage,
    appname: Config.appname
});

const sentryInitObject = {
    sampleRate: 0.1,
    tracesSampleRate: 0.1,
    environment: import.meta.env.VITE_ENVIRONMENT,
    debug: false,
    dsn: NetworkConfig.sentryUrl,
    integrations: [
        new Integrations.BrowserTracing({
            routingInstrumentation: Sentry.reactRouterV5Instrumentation(history)
        })
    ]
};

const App: React.FC = () => {
    const [isInitializing, setIsInitializing] = useState(true);
    const [consentGiven, setConsentGiven] = useState<boolean>(() => hasConsent());

    useEffect(() => {
        const onAccepted = () => setConsentGiven(true);
        window.addEventListener("consent:accepted", onAccepted);
        return () => window.removeEventListener("consent:accepted", onAccepted);
    }, []);

    useEffect(() => {
        const initialize = async () => {
            try {
                const { data: config } = await Axios.getInstance().get(`${window.location.origin}/config.json`);

                NetworkConfig.environment = config.env;
                NetworkConfig.wwPortalClientId = config.wwPortalClientId;
                NetworkConfig.cidaasBaseUrl = config.cidaasBaseUrl;

                // Stage-agnostic URL resolution: prefer explicit keys from
                // config.json, fall back to the Azure naming convention used
                // by dev/test/prod. Enables stages that don't map onto Azure
                // resource names — see Stages/Local.md#imp-local-01.
                if (config.authWebsiteUrl) {
                    NetworkConfig.authWebsiteUrl = config.authWebsiteUrl;
                } else if (config.env !== "prod") {
                    NetworkConfig.authWebsiteUrl = `https://${config.env}.auth.wwportal.ch`;
                }

                NetworkConfig.graphqlUrl = config.graphqlUrl
                    ?? `https://ww-${config.env}-hasura-cidaas-wa.azurewebsites.net/v1/graphql`;
                NetworkConfig.graphqlWssUrl = config.graphqlWssUrl
                    ?? `wss://ww-${config.env}-hasura-cidaas-wa.azurewebsites.net/v1/graphql`;

                initializeApolloClient(rootStore);

                if (config.Sentry.Active) {
                    const sampleRate = config.Sentry.SampleRate;
                    Sentry.init({
                        ...sentryInitObject,
                        sampleRate: sampleRate ?? 0.1,
                        tracesSampleRate: sampleRate ?? 0.1
                    });
                }

                if (config.maintenanceNoRedirectSubdomains && config.maintenanceNoRedirectSubdomains.length > 0) {
                    NetworkConfig.isMaintenanceActive = true;

                    const host = window.location.host.split(".")[0];
                    if (!config.maintenanceNoRedirectSubdomains.includes(host)) {
                        history.push(
                            `${Route.userInfoPage}${
                                config.maintenanceEndTime ? `?end=${config.maintenanceEndTime}` : ""
                            }`
                        );
                    }
                }
            } catch (error) {
                Sentry.init(sentryInitObject);
                console.error("Error fetching config:", error);
            } finally {
                setIsInitializing(false);
            }
        };

        initialize();
    }, []);

    useEffect(() => {
        rootStore.navStore.setHistory(history);
    }, []);

    const fallback = useCallback(
        ({ error, componentStack }: { error: Error; componentStack: string | null }) => (
            <Error error={error} componentStack={componentStack} />
        ),
        []
    );

    if (isInitializing) {
        return (
            <UI.Container className="d-flex flex-fill justify-content-center align-items-center fullscreen">
                <UI.Row>
                    <UI.Col className="text-center">
                        <UI.RotatingSpinner noLogo />
                    </UI.Col>
                </UI.Row>
            </UI.Container>
        );
    }

    return (
        <Suspense fallback={<Loading fullscreen />}>
            <RootStoreContext.Provider value={rootStore}>
                <Sentry.ErrorBoundary fallback={fallback}>
                    <GraphqlClient>
                        <Notifier />
                        <NotificationContainer position="bottom-left" newestOnTop={true} />
                        <Router history={history}>
                            <Main />
                            <Footer />
                            {consentGiven && <UserRatingModal />}
                            {consentGiven && <UserRatingModalAssembly />}
                        </Router>
                    </GraphqlClient>
                </Sentry.ErrorBoundary>
            </RootStoreContext.Provider>
        </Suspense>
    );
};

export default Sentry.withProfiler(App);
